import { Helmet } from 'react-helmet-async';

interface StructuredDataProps {
  type: 'home' | 'enquiries' | 'about';
}

const SITE_URL = 'https://kidsvids.com.au';

export default function StructuredData({ type }: StructuredDataProps) {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Kids Vids',
    url: SITE_URL,
    logo: `${SITE_URL}/kids-vids-small-banner-logo.png`,
    description:
      'Professional family video editing service in Adelaide. Transform your footage into beautiful narrative films.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Adelaide',
      addressRegion: 'SA',
      addressCountry: 'AU',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      areaServed: 'AU',
      availableLanguage: 'English',
    },
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Kids Vids',
    image: `${SITE_URL}/kids-vids-small-banner-logo.png`,
    '@id': `${SITE_URL}#organization`,
    url: SITE_URL,
    telephone: '',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Adelaide',
      addressRegion: 'SA',
      addressCountry: 'AU',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -34.9285,
      longitude: 138.6007,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Australia',
    },
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'Working with Children Check',
      },
      {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'Police Clearance',
      },
    ],
    description:
      'Family-run video editing service in Adelaide. We transform your family footage into beautiful narrative films - visual time capsules for your family.',
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Video Editing',
    provider: {
      '@id': `${SITE_URL}#organization`,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Australia',
    },
    description:
      'Professional family video editing service. We edit your footage into narrative films perfect for milestones, gifts, and preserving family memories.',
    offers: {
      '@type': 'Offer',
      description: 'Family video editing and narrative film creation',
    },
  };

  const breadcrumbSchemas: Record<string, any> = {
    home: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL,
        },
      ],
    },
    enquiries: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Enquiries',
          item: `${SITE_URL}/enquiries`,
        },
      ],
    },
    about: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Our Story',
          item: `${SITE_URL}/about`,
        },
      ],
    },
  };

  const schemas = [
    organizationSchema,
    localBusinessSchema,
    serviceSchema,
    breadcrumbSchemas[type],
  ];

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
}
